import { NavLink } from 'react-router-dom';
import { useAppStore } from '../../stores/appStore';
import { Settings, PanelLeftClose, PanelLeftOpen, LogOut, X } from 'lucide-react';
import { initials, navSections } from './nav';
import { useAuthStore } from '../../stores/authStore';
import { useIsDesktop } from '../../lib/useMediaQuery';
import { Logo } from '../ui/Logo';
import { ThemeToggle } from '../ui/ThemeToggle';
import { clsx } from 'clsx';

interface SidebarProps {
  /** Mobile only: whether the drawer is open. Ignored on desktop. */
  mobileOpen: boolean;
  onMobileClose: () => void;
}

/**
 * Desktop: fixed, collapsible rail. Mobile (< lg): slide-in drawer over a
 * backdrop, always expanded regardless of the persisted collapsed state.
 */
export function Sidebar({ mobileOpen, onMobileClose }: SidebarProps) {
  const { sidebarCollapsed, toggleSidebar } = useAppStore();
  const { user, signOut } = useAuthStore();
  const isDesktop = useIsDesktop();
  const collapsed = isDesktop && sidebarCollapsed;

  const linkClass = (isActive: boolean) =>
    clsx(
      'flex items-center gap-3 rounded-lg px-3 py-2.5 text-[14px] transition-colors duration-150 lg:py-2 lg:text-[13.5px]',
      collapsed && 'justify-center px-0',
      isActive ? 'bg-dark-elevated text-primary' : 'text-dark-muted hover:bg-dark-hover hover:text-dark-text',
    );

  return (
    <>
      {/* Mobile backdrop */}
      <div
        onClick={onMobileClose}
        aria-hidden
        className={clsx(
          'fixed inset-0 z-40 bg-black/60 transition-opacity duration-200 lg:hidden',
          mobileOpen ? 'opacity-100' : 'pointer-events-none opacity-0',
        )}
      />

      <aside
        className={clsx(
          'fixed inset-y-0 left-0 z-50 flex h-dvh flex-col border-r border-dark-border bg-dark-bg',
          'w-[min(18rem,85vw)] transition-[transform,width] duration-200 lg:translate-x-0',
          mobileOpen ? 'translate-x-0' : '-translate-x-full',
          collapsed ? 'lg:w-16' : 'lg:w-56',
        )}
        aria-label="Primary"
      >
        <div className={clsx('flex h-14 shrink-0 items-center border-b border-dark-border', collapsed ? 'justify-center' : 'justify-between px-4')}>
          <Logo size={22} withText={!collapsed} />
          <button onClick={onMobileClose} className="grid h-8 w-8 place-items-center rounded-md text-dark-muted hover:bg-dark-hover hover:text-dark-text lg:hidden" aria-label="Close menu">
            <X size={16} />
          </button>
        </div>

        <nav className="flex-1 space-y-5 overflow-y-auto px-2.5 py-4">
          {navSections.map((section) => (
            <div key={section.label}>
              {!collapsed && <div className="eyebrow mb-2 px-3 text-dark-faint">{section.label}</div>}
              <div className="space-y-px">
                {section.items.map(({ to, icon: Icon, label }) => (
                  <NavLink key={to} to={to} title={collapsed ? label : undefined} onClick={onMobileClose} className={({ isActive }) => linkClass(isActive)}>
                    <Icon size={16} strokeWidth={1.75} className="shrink-0" />
                    {!collapsed && <span>{label}</span>}
                  </NavLink>
                ))}
              </div>
            </div>
          ))}
        </nav>

        <div className="space-y-px border-t border-dark-border p-2.5 pb-[max(0.625rem,env(safe-area-inset-bottom))]">
          <NavLink to="/settings" title="Settings" onClick={onMobileClose} className={({ isActive }) => linkClass(isActive)}>
            <Settings size={16} strokeWidth={1.75} className="shrink-0" />
            {!collapsed && <span>Settings</span>}
          </NavLink>

          <div className={clsx('mt-1 flex items-center gap-2.5 rounded-lg px-2 py-2', collapsed && 'justify-center px-0')}>
            <div className="grid h-7 w-7 shrink-0 place-items-center rounded-full bg-primary text-[11px] font-semibold text-on-primary tabular">{initials(user?.name, user?.email)}</div>
            {!collapsed && (
              <div className="min-w-0 flex-1">
                <div className="truncate text-[13px]">{user?.name || 'Trader'}</div>
                <div className="truncate text-[11px] text-dark-faint tabular">{user?.email || 'Signed in'}</div>
              </div>
            )}
            {!collapsed && (
              <button onClick={() => void signOut()} title="Sign out" className="grid h-7 w-7 shrink-0 cursor-pointer place-items-center rounded-md text-dark-faint transition-colors hover:bg-dark-hover hover:text-dark-text">
                <LogOut size={14} />
              </button>
            )}
          </div>

          <div className={clsx('flex items-center', collapsed ? 'flex-col gap-1' : 'justify-between px-1')}>
            <ThemeToggle />
            <button onClick={toggleSidebar} className="hidden h-7 w-7 cursor-pointer place-items-center rounded-md text-dark-faint transition-colors hover:bg-dark-hover hover:text-dark-text lg:grid" aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}>
              {collapsed ? <PanelLeftOpen size={15} /> : <PanelLeftClose size={15} />}
            </button>
          </div>
        </div>
      </aside>
    </>
  );
}
